import { useState, useEffect, useMemo, useRef } from 'react';
import { message, Tooltip, Flex } from 'antd';
import type {
  LBNodeData,
  TextTemplateChunk,
  VariableRef,
} from '@/proto/logiblocks/logiblocks';
import type { CustomizationComponent } from '@/pages/Logiblocks/types';
import { TemplatedTextArea } from '@/pages/Logiblocks/variables/TemplatedTextArea/TemplatedTextArea';
import { UserMentionSource } from '@/pages/Logiblocks/variables/TemplatedTextArea/mentionList';
import { getMentionedUserIds } from '@/pages/Logiblocks/variables/TemplatedTextArea/templateSerialization';
import {
  FieldErrorLabel,
  FieldLabel,
  ShipmentField,
  useHasUpstreamAlert,
} from '../shared';
import ToggleSwitch from '@/components/ToggleSwitch';
import { AddCocNoteActionFields } from './AddCocNoteActionNodeInfo';
import styles from '../NodeCustomization.module.css';
import useToken from '@/common/helper/useToken';
import { GetTeamsWithSearchAndPagination } from '@/apis/settings';
import SpecificTeamsContext from '@/pages/Shipments/partials/COC_Privacy_Comments/SpecificTeamsContext';
import AudienceSelector, {
  type AudienceMeta,
  type AudienceType,
} from '@/pages/Shipments/partials/COC_Privacy_Comments/AudienceSelector';
import { BrackedAddIcon } from '@/assets/icons/icons';
import { AllCommonTeams } from '@/pages/Shipments/partials/ReplyCommentBox';

export const AddCocNoteActionCustomization: CustomizationComponent = ({
  nodeId,
  nodeData,
  onChange,
  isReadOnly = false,
  validationErrors,
}) => {
  const shipmentError = validationErrors?.find(
    (e) => e.field === AddCocNoteActionFields.SHIPMENT
  );
  const messageError = validationErrors?.find(
    (e) => e.field === AddCocNoteActionFields.MESSAGE_TEXT
  );
  const cocNote = nodeData.add_coc_note_action;

  const { getToken } = useToken();
  const [shipmentId, setShipmentId] = useState<VariableRef | undefined>(
    cocNote?.shipment_id
  );
  const [audience, setAudience] = useState<AudienceType>(
    (cocNote?.audience as AudienceType) || 'public'
  );
  const [selectedTeamIDs, setSelectedTeamIDs] = useState<string[]>(
    cocNote?.team_ids || []
  );
  const hasUpstreamAlert = useHasUpstreamAlert(nodeId);
  const appendToAlert = cocNote?.append_to_alert ?? false;

  // `AudienceSelector` resets its unapplied team ticks whenever `custody`
  // changes. A new object on every render would clear them mid-selection.
  const custody = useMemo(
    () => ({
      audience_type: audience,
      audience_teams: selectedTeamIDs.map((id) => ({ id })),
    }),
    [audience, selectedTeamIDs]
  );

  // Reference to trigger variable picker programmatically
  const triggerPickerRef = useRef<(() => void) | null>(null);

  const [specificTeamsList, setSpecificTeamsList] = useState<AllCommonTeams[]>(
    []
  );
  const [hasMoreTeams, setHasMoreTeams] = useState<boolean>(false);
  const [loadingTeams, setLoadingTeams] = useState<boolean>(false);

  const fetchSpecificTeams = async (
    pageToFetch: number,
    searchQuery: string,
    append = false
  ) => {
    const token = getToken();
    if (!token) return;

    setLoadingTeams(true);
    try {
      const response = await GetTeamsWithSearchAndPagination(
        token,
        searchQuery,
        pageToFetch
      );
      const resData = response?.data?.data;
      if (resData?.is_success) {
        const newTeams = resData.data || [];
        setSpecificTeamsList((prev) =>
          append ? [...prev, ...newTeams] : newTeams
        );
        const totalPages = resData.page_count || 1;
        setHasMoreTeams(pageToFetch < totalPages);
      } else {
        message.error(resData?.message || 'Failed to fetch teams');
      }
    } catch (err) {
      console.error('fetchSpecificTeams error:', err);
    } finally {
      setLoadingTeams(false);
    }
  };

  const handleVariablesIconClick = () => {
    triggerPickerRef.current?.();
  };

  useEffect(() => {
    const d = nodeData.add_coc_note_action;
    setShipmentId(d?.shipment_id);
    setAudience((d?.audience as AudienceType) || 'public');
    setSelectedTeamIDs(d?.team_ids || []);
  }, [nodeData]);

  /** Writes the node's data with `patch` applied. "My team" clears the team ids. */
  const emitChange = (patch: {
    shipment_id?: VariableRef | undefined;
    message_chunks?: TextTemplateChunk[];
    audience?: AudienceType;
    team_ids?: string[];
    mentioned_user_ids?: string[];
    append_to_alert?: boolean;
  }) => {
    const finalAudience =
      patch.audience !== undefined ? patch.audience : audience;
    let finalTeamIds =
      patch.team_ids !== undefined ? patch.team_ids : selectedTeamIDs;

    if (finalAudience === 'my_team') {
      finalTeamIds = [];
    }

    const updatedData: Partial<LBNodeData> = {
      add_coc_note_action: {
        shipment_id: shipmentId,
        message_chunks: cocNote?.message_chunks ?? [],
        mentioned_user_ids: cocNote?.mentioned_user_ids ?? [],
        append_to_alert: appendToAlert,
        ...patch,
        audience: finalAudience,
        team_ids: finalTeamIds,
      },
    };
    onChange(updatedData);
  };

  const handleShipmentIdChange = (ref: VariableRef | null) => {
    const value = ref ?? undefined;
    setShipmentId(value);
    emitChange({ shipment_id: value });
  };

  /** Writes an edited message as chunks, with the users it mentions. */
  const handleMessageChange = (chunks: TextTemplateChunk[]) => {
    emitChange({
      message_chunks: chunks,
      mentioned_user_ids: getMentionedUserIds(chunks),
    });
  };

  /** Writes the audience the author picked, and the teams it names. */
  const handleAudienceChange = (meta: AudienceMeta) => {
    const teamIds = meta.audience === 'my_team' ? [] : (meta.teamIds ?? []);
    setAudience(meta.audience);
    setSelectedTeamIDs(teamIds);
    emitChange({ audience: meta.audience, team_ids: teamIds });
  };

  return (
    <SpecificTeamsContext.Provider
      value={{
        enableLoadMoreTeams: true,
        hasMoreTeams,
        loadingTeams,
        onFetchSpecificTeams: fetchSpecificTeams,
        specificTeams: specificTeamsList,
      }}
    >
      <ShipmentField
        nodeId={nodeId}
        value={shipmentId}
        onChange={handleShipmentIdChange}
        disabled={isReadOnly}
        error={shipmentError}
      />

      <div className={styles.customizationSection}>
        <Flex align='center' gap={4} style={{ marginBottom: '8px' }}>
          <FieldLabel
            required
            className={styles.sectionLabel}
            style={{ marginBottom: 0 }}
          >
            Custom note
          </FieldLabel>
          {!isReadOnly && (
            <Tooltip
              title='Insert variable ({)'
              styles={{
                container: {
                  fontSize: '10px',
                },
              }}
            >
              <button
                type='button'
                className={styles.iconButton}
                onClick={handleVariablesIconClick}
                aria-label='Insert variable'
              >
                <BrackedAddIcon />
              </button>
            </Tooltip>
          )}
        </Flex>
        <TemplatedTextArea
          nodeId={nodeId}
          chunks={cocNote?.message_chunks ?? []}
          richText
          userMentionSource={UserMentionSource.COMMENT_MENTIONABLES}
          onChange={handleMessageChange}
          placeholder='Write a comment'
          pickerAsModal
          disabled={isReadOnly}
          onTriggerPickerRef={(fn) => {
            triggerPickerRef.current = fn;
          }}
          toolbarLeadingContent={
            <AudienceSelector
              myTeams={[]}
              specificTeams={[]}
              defaultAudience={audience}
              hideIfSingleTeam={false}
              onChange={handleAudienceChange}
              setSelectedTeamID={setSelectedTeamIDs}
              custody={custody}
            />
          }
        />
        <FieldErrorLabel error={messageError} />
      </div>

      <div className={styles.customizationSection}>
        <Flex align='center' gap={8}>
          <ToggleSwitch
            alignLeft
            checked={hasUpstreamAlert && appendToAlert}
            onChange={(event) =>
              emitChange({ append_to_alert: event.target.checked })
            }
            disabled={isReadOnly || !hasUpstreamAlert}
          />
          <Tooltip
            title={
              hasUpstreamAlert
                ? undefined
                : 'Available when the workflow is triggered by an alert'
            }
          >
            <span>
              <FieldLabel
                className={
                  hasUpstreamAlert
                    ? styles.toggleLabel
                    : `${styles.toggleLabel} ${styles.toggleLabelDisabled}`
                }
              >
                Append to alert
              </FieldLabel>
            </span>
          </Tooltip>
        </Flex>
      </div>
    </SpecificTeamsContext.Provider>
  );
};
